import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Doc0SubjectNote,
  Doc0DiscountDraft,
  Doc1MasterNote,
  Doc2Pledge,
  Doc3UCC,
  Doc4Notice,
  Doc5Resolution,
  Doc6PurchaseAssignment,
  Doc6AAllonge,
  Doc6BBillOfSale,
  Doc6CNoticeToMaker,
  Doc6DClosingCert,
  Doc6EIncumbency,
  DocT01CertificateOfTrust,
  DocT02TrueSale,
  DocT03KYCMemo,
  DocT04TrustResolution,
  DocT05Custody,
  DocT06TrueSaleOpinion,
  DocD01DealerTermSheet,
  Doc6FProceedsDirection,
  Doc6GDisbursementAck,
  DocCR01CollateralAssignment,
  DocCR02UCC1Creditor,
  DocCR03PayoffTender,
  DocCR04NoticeOfTender,
  DocCR05AssignmentInSatisfaction,
  DocCR00PromissoryNote,
  DocCR00BankersAcceptance,
  DocCR00Allonge,
  DocCR06ControlPledgeAck,
  DocCR07MutualRelease,
  DocCR08PayoffLienRelease,
  DocCR09SatisfactionCertificate,
} from "../components/documents/Documents";
import { ExecutionBlock } from "../components/documents/_shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CREDITOR_DOC_MAP = {
  DocCR01CollateralAssignment,
  DocCR02UCC1Creditor,
  DocCR03PayoffTender,
  DocCR04NoticeOfTender,
  DocCR05AssignmentInSatisfaction,
  // Use the SAME Promissory Note + Private Negotiable Instrument as the structured-deal section.
  DocCR00PromissoryNote: Doc0SubjectNote,
  DocCR00BankersAcceptance: Doc0DiscountDraft,
  DocCR00Allonge,
  DocCR06ControlPledgeAck,
  DocCR07MutualRelease,
  DocCR08PayoffLienRelease,
  DocCR09SatisfactionCertificate,
};

// Creditor execution pages show the Managing Trustee in his trust capacity.
const CREDITOR_ROLE_MAP = { seller: "Managing Trustee", trustee: "Independent Co-Trustee" };

const SPV_TRUST_DOCS = [
  // Legal-execution order: authority → entity → compliance → operative → opinion → custody → commercial
  ["DocT04TrustResolution", DocT04TrustResolution],      // Tab 1 — Board authority to act
  ["DocT01CertificateOfTrust", DocT01CertificateOfTrust],   // Tab 2 — Proof of entity formation
  ["DocT03KYCMemo", DocT03KYCMemo],              // Tab 3 — Compliance gate
  ["DocT02TrueSale", DocT02TrueSale],             // Tab 4 — Operative agreement
  ["DocT06TrueSaleOpinion", DocT06TrueSaleOpinion],      // Tab 5 — Legal opinion blessing #4
  ["DocT05Custody", DocT05Custody],              // Tab 6 — Post-transfer custody
  ["DocD01DealerTermSheet", DocD01DealerTermSheet],      // Tab 7 — Resale economics
];

const FINANCING_DOCS = [
  // Authority → operative → perfection → notice → collateral exhibit
  ["Doc5Resolution", Doc5Resolution],             // Tab 1 — Board authority
  ["Doc1MasterNote", Doc1MasterNote],             // Tab 2 — Operative loan note
  ["Doc2Pledge", Doc2Pledge],                 // Tab 3 — Pledge agreement
  ["Doc3UCC", Doc3UCC],                    // Tab 4 — UCC-1 perfection filing
  ["Doc4Notice", Doc4Notice],                 // Tab 5 — Notice to Maker (locked-box)
  ["Doc0SubjectNote", Doc0SubjectNote],            // Tab 6 — Subject Note (Exhibit A: collateral)
];

const SALE_DOCS = [
  // Authority → reps & warranties → operative transfer → endorsement → bill of sale → notices → proceeds → exhibits
  ["Doc6EIncumbency", Doc6EIncumbency],            // Tab 1 — Officer authority
  ["Doc6DClosingCert", Doc6DClosingCert],           // Tab 2 — Closing reps & warranties
  ["Doc6PurchaseAssignment", Doc6PurchaseAssignment],     // Tab 3 — Operative purchase & assignment
  ["Doc6AAllonge", Doc6AAllonge],            // Tab 4 — Endorsement (allonge to note)
  ["Doc6BBillOfSale", Doc6BBillOfSale],            // Tab 5 — Bill of sale
  ["Doc6CNoticeToMaker", Doc6CNoticeToMaker],        // Tab 6 — Notice to Maker
  ["Doc6FProceedsDirection", Doc6FProceedsDirection],     // Tab 7 — Proceeds direction
  ["Doc6GDisbursementAck", Doc6GDisbursementAck],       // Tab 8 — Disbursement acknowledgment
  ["Doc0SubjectNote", Doc0SubjectNote],            // Tab 9 — Subject Note (Exhibit A)
  ["Doc0DiscountDraft", Doc0DiscountDraft],          // Tab 10 — Private Negotiable Instrument draft (Exhibit B)
  ["DocD01DealerTermSheet", DocD01DealerTermSheet],      // Tab 11 — Dealer term sheet (commercial summary)
];

const DOCS_BY_PATH = {
  spv_trust: SPV_TRUST_DOCS,
  financing: FINANCING_DOCS,
  discount_sale: SALE_DOCS,
};

const PATH_LABEL = {
  spv_trust: "SPV Trust Setup",
  financing: "Collateralized Loan",
  discount_sale: "Outright Note Sale",
};

/**
 * Full Closing Binder — renders every legal document for a given path
 * inline as paper-docs. Designed to be:
 *   (a) human-readable via a share-link
 *   (b) PDF-rendered by headless Chrome for the email/download binder
 *
 * Hydrates from /api/print/{tx_id}?t=<jwt>. Signals readiness for the
 * headless Chrome capture by setting document.title once mounted.
 */
export default function PrintBinder() {
  const { tx_id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.remove("dark-mode"); // binder always prints on white
    // Remove the Emergent platform badge so it doesn't appear in the PDF
    const stripBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) badge.remove();
    };
    stripBadge();
    const i = setInterval(stripBadge, 500);
    setTimeout(() => clearInterval(i), 5000);
    if (!token) {
      setError("Missing token — this URL is only valid with a signed `t` query parameter.");
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/print/${tx_id}?t=${encodeURIComponent(token)}`);
        setData(r.data);
        document.title = `Closing Binder — ${r.data.trust_name || tx_id}`;
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [tx_id, token]);

  if (error) {
    return (
      <div style={{ padding: 60, fontFamily: "Manrope, sans-serif", color: "#9E2A2B" }}>
        <h1 style={{ fontSize: 24, marginBottom: 12 }}>Print payload unavailable</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 60, fontFamily: "Manrope, sans-serif", color: "#5C5C5C" }}>
        Loading binder…
      </div>
    );
  }

  const { deal: dealRaw, path, signatures = [], signed_binder_sha256: hash, events = [] } = data;
  // Inject signatures into the deal so every doc can render the captured
  // click-to-sign attestation inline (cursive /s/ + notary seal) instead of
  // showing blank "By: ____" lines.
  // Surface the most recent Secure Payload transmission so the Closing &
  // Satisfaction Certificate can stamp the delivered binder's SHA-256 + the
  // transport security (mTLS / TLS + HMAC-SHA256) as court-ready evidence.
  const lastTransmit = [...events].reverse().find((e) => e.type === "secure_payload_transmitted");
  const secureTransmission = lastTransmit ? {
    hash: lastTransmit.data?.hash || hash || "",
    mtls: !!lastTransmit.data?.mtls,
    ok: !!lastTransmit.data?.ok,
    statusCode: lastTransmit.data?.status_code,
    transmittedAt: lastTransmit.timestamp,
    webhookUrl: lastTransmit.data?.webhook_url || "",
  } : null;
  const deal = { ...dealRaw, signatures, secureTransmission };
  const baseDocs = DOCS_BY_PATH[path] || SPV_TRUST_DOCS;
  // Append Direct-to-Creditor instruments when a creditor direction is set.
  const creditorInstruments = (deal.creditorDirection?.instruments || [])
    .map((name) => [name, CREDITOR_DOC_MAP[name]])
    .filter(([, Comp]) => !!Comp);
  const docs = [...baseDocs, ...creditorInstruments];

  // Single-instrument render (e.g. the completed Private Negotiable Instrument) for
  // embedding into fax/download packages via headless Chrome.
  const only = params.get("only");
  if (only) {
    const ONLY_MAP = { Doc0DiscountDraft, Doc0SubjectNote, ...CREDITOR_DOC_MAP };
    const OnlyDoc = ONLY_MAP[only];
    return (
      <div className="paper-canvas" style={{ background: "#ECECE4", padding: "32px 0" }} data-testid="print-binder-only">
        {OnlyDoc ? <OnlyDoc d={deal} /> : <div style={{ padding: 60 }}>Unknown document: {only}</div>}
      </div>
    );
  }

  // Friendly display labels per document type for the execution-page heading
  const DOC_LABELS = {
    Doc6EIncumbency: "Officer Incumbency Certificate",
    Doc6DClosingCert: "Seller's Closing Certificate",
    Doc6PurchaseAssignment: "Note Purchase & Assignment Agreement",
    Doc6AAllonge: "Allonge / Endorsement",
    Doc6BBillOfSale: "Bill of Sale",
    Doc6CNoticeToMaker: "Notice to Maker",
    Doc6FProceedsDirection: "Proceeds Direction & Disbursement Letter",
    Doc6GDisbursementAck: "Disbursement Acknowledgment",
    Doc0SubjectNote: "Subject Note (Exhibit A)",
    Doc0DiscountDraft: "Private Negotiable Instrument (Exhibit B)",
    DocD01DealerTermSheet: "Dealer Term Sheet",
    DocT01CertificateOfTrust: "Certificate of Trust",
    DocT02TrueSale: "True-Sale Trust Agreement",
    DocT03KYCMemo: "KYC / OFAC Memo",
    DocT04TrustResolution: "Trust Resolution",
    DocT05Custody: "Custody Agreement",
    DocT06TrueSaleOpinion: "True-Sale Legal Opinion",
    Doc1MasterNote: "Master Promissory Note",
    Doc2Pledge: "Note Pledge Agreement",
    Doc3UCC: "UCC-1 Financing Statement",
    Doc4Notice: "Notice to Maker (Locked-Box)",
    Doc5Resolution: "Board Resolution",
    DocCR01CollateralAssignment: "Collateral Assignment & Security Agreement",
    DocCR02UCC1Creditor: "UCC-1 Financing Statement (Creditor)",
    DocCR03PayoffTender: "Instruction to Apply Proceeds / Payoff Tender",
    DocCR04NoticeOfTender: "Notice of Tender & Conditional Acceptance",
    DocCR05AssignmentInSatisfaction: "Assignment of Instrument in Satisfaction",
    DocCR00PromissoryNote: "Promissory Note (Subject Note)",
    DocCR00BankersAcceptance: "Private Negotiable Instrument",
    DocCR00Allonge: "Allonge & Endorsement",
    DocCR06ControlPledgeAck: "Account Control & Acknowledgment of Pledge",
    DocCR07MutualRelease: "Mutual Release & Accord-and-Satisfaction",
    DocCR08PayoffLienRelease: "Payoff & Lien Release Acknowledgment",
    DocCR09SatisfactionCertificate: "Closing & Satisfaction Certificate",
  };

  // Docs that require notary acknowledgement under standard practice
  const NOTARY_REQUIRED_DOCS = new Set([
    "Doc6PurchaseAssignment",
    "Doc6BBillOfSale",
    "Doc6AAllonge",
    "Doc6DClosingCert",
    "DocT01CertificateOfTrust",
    "DocT02TrueSale",
    "DocT04TrustResolution",
    "Doc5Resolution",
    "Doc2Pledge",
    "DocCR01CollateralAssignment",
    "DocCR05AssignmentInSatisfaction",
    "DocCR00PromissoryNote",
    "DocCR00BankersAcceptance",
    "DocCR00Allonge",
    "DocCR06ControlPledgeAck",
    "DocCR07MutualRelease",
    "DocCR08PayoffLienRelease",
    "DocCR09SatisfactionCertificate",
  ]);

  return (
    <div className="paper-canvas" style={{ background: "#ECECE4", padding: "32px 0" }} data-testid="print-binder">
      <Link to="/" data-testid="printbinder-home-link" className="no-print" style={{ position: "fixed", top: 16, left: 16, zIndex: 50, background: "#13130E", color: "#ECECE4", padding: "8px 14px", borderRadius: 6, fontSize: 12, textDecoration: "none", border: "1px solid #2A2A22" }}>← Return to Home</Link>
      {/* Cover page */}
      <div className="paper-doc print-binder-cover" data-testid="print-binder-cover">
        <div style={{ textAlign: "center", marginTop: 80 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 24 }}>
            Closing Binder
          </div>
          <h1 className="doc-title" style={{ fontSize: 32 }}>{deal.trustName || deal.borrowerName}</h1>
          <h2 className="doc-sub">{PATH_LABEL[path] || path}</h2>
          <div style={{ marginTop: 48, fontSize: 14, color: "#5C5C5C" }}>
            Face Value · <span style={{ color: "#0A0A0A", fontWeight: 600 }}>${Number(deal.faceValue || 0).toLocaleString()}</span>
          </div>
          <div style={{ fontSize: 14, color: "#5C5C5C", marginTop: 4 }}>
            Purchase Price · <span style={{ color: "#0A0A0A", fontWeight: 600 }}>${Number(deal.purchasePrice || 0).toLocaleString()}</span>
          </div>
          <div style={{ fontSize: 14, color: "#5C5C5C", marginTop: 4 }}>
            Execution Date · <span style={{ color: "#0A0A0A" }}>{deal.executionDate}</span>
          </div>
          <div style={{ fontSize: 14, color: "#5C5C5C", marginTop: 4 }}>
            Maturity Date · <span style={{ color: "#0A0A0A" }}>{deal.maturityDate}</span>
          </div>
          <div style={{ fontSize: 14, color: "#5C5C5C", marginTop: 4 }} data-testid="print-binder-updated-date">
            Last Updated · <span style={{ color: "#0A0A0A" }}>{new Date(data.updated_at || (events.length ? events[events.length - 1].at : Date.now())).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
          {signatures.length > 0 && (
            <div style={{ marginTop: 32, fontSize: 12, color: "#1a3a8e", fontWeight: 600, letterSpacing: "0.1em" }}>
              EXECUTED · {signatures.length} SIGNATURE{signatures.length !== 1 ? "S" : ""} CAPTURED
            </div>
          )}
          <div style={{ marginTop: 96, fontSize: 11, color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.06em" }}>
            tx · {tx_id}
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace" }}>
            Generated · {new Date().toISOString()}
          </div>
        </div>
      </div>

      {/* Each document — rendered DIRECTLY (no wrapper div) so the print
          CSS `.paper-doc + .paper-doc { page-break-before: always }`
          actually fires. Wrapping each in a div made every paper-doc a
          `:last-child` of its wrapper, which silently killed page breaks. */}
      {docs.map((entry, idx) => {
        const [docKey, Doc] = entry;
        const friendlyLabel = DOC_LABELS[docKey] || docKey;
        return (
        <React.Fragment key={idx}>
          <Doc d={deal} />
          {signatures.length > 0 && (
            <div className="paper-doc" data-testid={`exec-page-${idx}`}>
              <div style={{ fontSize: "9.5pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 8 }}>
                Doc {String(idx + 1).padStart(2, "0")} · {friendlyLabel} — Execution Page
              </div>
              <ExecutionBlock
                signatures={signatures}
                txId={tx_id}
                hash={hash}
                requireNotary={NOTARY_REQUIRED_DOCS.has(docKey)}
                roleMap={docKey.startsWith("DocCR") ? CREDITOR_ROLE_MAP : undefined}
              />
            </div>
          )}
        </React.Fragment>
        );
      })}

      {/* ============ Chain of Custody / Audit Trail ============ */}
      <div className="paper-doc" data-testid="binder-audit-trail">
        <div style={{ fontSize: "9.5pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 8 }}>
          Chain of Custody · Audit Trail
        </div>
        <h2 className="doc-title" style={{ fontSize: 20, marginBottom: 4 }}>Lifecycle &amp; Execution Record</h2>
        <p style={{ fontSize: "9pt", color: "#5C5C5C", marginBottom: 18 }}>
          Immutable record of every lifecycle event recorded for this transaction, in chronological order.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8.5pt" }}>
          <thead>
            <tr style={{ borderBottom: "1.5px solid #0A0A0A", textAlign: "left" }}>
              <th style={{ padding: "6px 4px", width: "20%" }}>Timestamp (UTC)</th>
              <th style={{ padding: "6px 4px", width: "26%" }}>Event</th>
              <th style={{ padding: "6px 4px" }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {[...events]
              .sort((a, b) => String(a.timestamp || "").localeCompare(String(b.timestamp || "")))
              .map((ev, i) => {
                const label = (ev.type || "event").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                const d = ev.data || {};
                const details = Object.entries(d)
                  .filter(([, v]) => v !== null && v !== "" && typeof v !== "object")
                  .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
                  .join(" · ");
                return (
                  <tr key={i} style={{ borderBottom: "0.5px solid #D8D8CE", verticalAlign: "top" }}>
                    <td style={{ padding: "5px 4px", fontFamily: "IBM Plex Mono, monospace", color: "#5C5C5C" }}>
                      {ev.timestamp ? new Date(ev.timestamp).toISOString().replace("T", " ").slice(0, 19) : "—"}
                    </td>
                    <td style={{ padding: "5px 4px", fontWeight: 600 }}>{label}</td>
                    <td style={{ padding: "5px 4px", color: "#3A3A3A" }}>{details || "—"}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        {signatures.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div style={{ fontSize: "8.5pt", letterSpacing: "0.14em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 6 }}>
              Signature Fingerprints (SHA-256)
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8pt" }}>
              <tbody>
                {signatures.map((s, i) => (
                  <tr key={i} style={{ borderBottom: "0.5px solid #D8D8CE" }}>
                    <td style={{ padding: "4px", fontWeight: 600, width: "30%" }}>{s.signer_name} <span style={{ color: "#8A8A8A", fontWeight: 400 }}>({s.signer_role})</span></td>
                    <td style={{ padding: "4px", fontFamily: "IBM Plex Mono, monospace", color: "#5C5C5C", wordBreak: "break-all" }}>{s.fingerprint || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hash && (
          <div style={{ marginTop: 18, fontSize: "8pt", color: "#5C5C5C", fontFamily: "IBM Plex Mono, monospace", wordBreak: "break-all" }}>
            Binder integrity hash (SHA-256): {hash}
          </div>
        )}
        <div style={{ marginTop: 14, fontSize: "8pt", color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace" }}>
          tx · {tx_id} · {events.length} event{events.length !== 1 ? "s" : ""} recorded
        </div>
      </div>
    </div>
  );
}
